import dayjs from 'dayjs';
import { NestedModule, NestedAssignment, UngradedParticipant, BulkEvaluationRow, BulkEvaluationRowStatus } from '../types/training';

// Characters that are unsafe in Azure Blob Storage path segments
export const UNSAFE_BLOB_CHARS = /[#%?\\*"<>|:&+]/;

/**
 * Returns an error message if the value contains characters that are unsafe
 * in Azure Blob Storage paths, or undefined if the value is clean.
 * Use as an Ant Design Form validator: { validator: validateBlobSafeName }
 */
export const validateBlobSafeName = (_: any, value: string): Promise<void> => {
    if (value && UNSAFE_BLOB_CHARS.test(value)) {
        return Promise.reject(
            new Error('The following characters are not valid for Training ID: # % ? \\ * " < > | : & +')
        );
    }
    return Promise.resolve();
};

const GENERIC_NAMES = new Set(['participant user', 'participant', 'user', 'unknown']);

export function formatDisplayName(fullname: string, email?: string): string {
    const isGeneric = !fullname || GENERIC_NAMES.has(fullname.toLowerCase().trim());
    if (!isGeneric) return fullname;
    if (!email) return fullname || 'Unknown';
    const local = email.split('@')[0];
    return local.split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

/**
 * Robustly parses a module ID string into its constituent parts.
 * Expected format: "Module Name [Description] #T30"
 * Description and #T suffix are optional.
 */
export const parseModuleId = (rawId: string) => {
    if (!rawId) return { name: '', description: '', estimatedTime: 30, attemptLimit: 0 };

    let namePart = rawId;
    let estimatedTime = 30;
    let attemptLimit = 0;

    const attemptMatch = namePart.match(/#A(\d+)/);
    if (attemptMatch) {
        attemptLimit = parseInt(attemptMatch[1]);
        namePart = namePart.replace(/#A\d+/, '').trim();
    }

    const timeMatch = namePart.match(/#T(\d+)/);
    if (timeMatch) {
        estimatedTime = parseInt(timeMatch[1]);
        namePart = namePart.substring(0, timeMatch.index).trim();
    }

    let description = '';
    const descMatch = namePart.match(/\[(.*?)\]/);
    if (descMatch) {
        description = descMatch[1].trim();
        namePart = namePart.replace(/\[(.*?)\]/, '').trim();
    }

    return { name: namePart, description, estimatedTime, attemptLimit };
};

/**
 * Strips HTML tags from a string and decodes basic entities.
 */
export const stripHtml = (html: string): string => {
    if (!html) return '';
    return html
        .replace(/<[^>]*>/g, '')
        .replace(/Subjective Assessment:\s*/gi, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
};

// ─── TrainerLed Hierarchy Skeleton ───────────────────────────────────────────
// Stored in Moodle's `assessments` field on `local_ai_assessment_prog` table.
// Only structure + moodleIds are stored. Full content (description, deadline,
// files) is fetched live from getManualModules and merged by moodleId.

export interface AssignmentSkeleton { mid: number; title: string; mc: number; }
export interface SubModuleSkeleton  { n: string; a: AssignmentSkeleton[]; }
export interface ModuleSkeleton     { n: string; t: 'structured' | 'direct' | 'mixed'; subs: SubModuleSkeleton[]; a: AssignmentSkeleton[]; }

/** Returns true if the assessments string is a TrainerLed skeleton JSON */
export const isTrainerLedSkeleton = (assessments: string): boolean => {
    if (!assessments) return false;
    const t = assessments.trim();
    return t.startsWith('[') && t.includes('"mid"');
};

/**
 * Serialize NestedModule[] → compact skeleton JSON for the assessments field.
 * Call after all Moodle assignments are created (moodleId populated).
 */
export const serializeTrainerLedSkeleton = (modules: NestedModule[]): string => {
    const skeleton: ModuleSkeleton[] = modules.map(mod => ({
        n: mod.name,
        t: mod.structureType || 'structured',
        subs: (mod.subModules || []).map(sub => ({
            n: sub.name,
            a: (sub.assignments || []).map(a => ({ mid: a.moodleId!, title: a.title, mc: a.maxCredits ?? 0 }))
        })),
        a: (mod.assignments || []).map(a => ({ mid: a.moodleId!, title: a.title, mc: a.maxCredits ?? 0 }))
    }));
    return JSON.stringify(skeleton);
};

/**
 * Deserialize skeleton JSON + live Moodle modules → full NestedModule[].
 * liveModules comes from getManualModules (has description, deadline, files).
 */
/**
 * Reconstruct NestedModule[] from live Moodle assignments using sectionname metadata.
 * sectionname format: "ModuleName::SubModuleName" (structured) or "ModuleName::__direct__" (direct).
 * Assignments without a sectionname are treated as legacy flat entries (no module grouping).
 */
export const reconstructFromLiveModules = (liveModules: any[]): NestedModule[] => {
    const moduleMap = new Map<string, NestedModule>();

    for (const a of liveModules) {
        const raw: string = a.sectionname || '';
        const sepIdx = raw.indexOf('::');

        // Legacy flat assignment — no sectionname stored
        if (!raw || sepIdx === -1) {
            const modName = '__legacy__';
            if (!moduleMap.has(modName)) {
                moduleMap.set(modName, {
                    id: '__legacy__', name: '', structureType: 'direct',
                    subModules: [], assignments: []
                });
            }
            (moduleMap.get(modName)!.assignments = moduleMap.get(modName)!.assignments || [])
                .push(hydrateAssignment(a));
            continue;
        }

        const modName = raw.substring(0, sepIdx);
        const subName = raw.substring(sepIdx + 2);

        if (!moduleMap.has(modName)) {
            moduleMap.set(modName, {
                id: modName, name: modName, structureType: 'structured',
                subModules: [], assignments: []
            });
        }
        const mod = moduleMap.get(modName)!;
        const modAssignments = mod.assignments || (mod.assignments = []);
        const modSubModules  = mod.subModules  || (mod.subModules  = []);

        if (subName === '__direct__') {
            modAssignments.push(hydrateAssignment(a));
        } else {
            let sub = modSubModules.find(s => s.name === subName);
            if (!sub) {
                sub = { id: `${modName}::${subName}`, name: subName, assignments: [] };
                modSubModules.push(sub);
            }
            sub.assignments.push(hydrateAssignment(a));
        }
    }

    // Infer structureType per module
    moduleMap.forEach(mod => {
        if (mod.name === '') return;
        const hasSubs   = (mod.subModules  || []).length > 0;
        const hasDirect = (mod.assignments || []).length > 0;
        mod.structureType = hasSubs && hasDirect ? 'mixed' : hasSubs ? 'structured' : 'direct';
    });

    const result: NestedModule[] = [];
    moduleMap.forEach(mod => result.push(mod));
    return result;
};

const hydrateAssignment = (a: any): NestedAssignment => ({
    id: String(a.id),
    moodleId: parseInt(a.id),
    title: a.name || a.title || '',
    description: a.content || a.description || '',
    deadline: a.deadline ? dayjs.unix(parseInt(a.deadline)) : null,
    maxCredits: a.maxcredits ?? 0,
    achievedCredits: a.grade >= 0 ? a.grade : undefined,
    stateDeadlines: a.statedeadlines ? (() => { try { return JSON.parse(a.statedeadlines); } catch { return undefined; } })() : undefined,
    evalCriteria: a.evalcriteria ? (() => { try { return JSON.parse(a.evalcriteria); } catch { return undefined; } })() : undefined,
    introfiles: a.introfiles || [],
    submissionfiles: a.submissionfiles || [],
    status: a.status,
    submissionid: a.submissionid,
    workflowEnabled: !!a.workflowenabled,
});

export const deserializeTrainerLedSkeleton = (
    skeletonJson: string,
    liveModules: any[]
): NestedModule[] => {
    let skeleton: ModuleSkeleton[];
    try {
        skeleton = JSON.parse(skeletonJson);
        if (!Array.isArray(skeleton)) return [];
    } catch { return []; }

    const liveById: Record<number, any> = {};
    liveModules.forEach(m => { liveById[parseInt(m.id)] = m; });

    const genId = () => Math.random().toString(36).substr(2, 9);

    const hydrate = (sk: AssignmentSkeleton): NestedAssignment => {
        const live = liveById[sk.mid];
        return {
            id: genId(),
            moodleId: sk.mid,
            title: sk.title,
            description: live ? (live.content || live.description || '') : '',
            deadline: live?.deadline ? dayjs.unix(parseInt(live.deadline)) : null,
            maxCredits: sk.mc ?? 0,
            introfiles: live?.introfiles || [],
            submissionfiles: live?.submissionfiles || [],
            status: live?.status,
            submissionid: live?.submissionid,
            achievedCredits: live?.grade,
            workflowEnabled: !!live?.workflowenabled,
        };
    };

    return skeleton.map(mod => ({
        id: genId(),
        name: mod.n,
        structureType: mod.t || 'structured',
        subModules: (mod.subs || []).map(sub => ({
            id: genId(),
            name: sub.n,
            assignments: (sub.a || []).map(hydrate)
        })),
        assignments: (mod.a || []).map(hydrate)
    }));
};

/**
 * Derives a submitted/total assignment count per participant from
 * getManualCourseProgress's raw response - used as the fallback list when
 * a trainer-led program's real leaderboard is empty because nobody's been
 * graded yet (see specs/2026-09-10-ungraded-participant-list.md).
 */
export const deriveUngradedParticipants = (
    courseProgress: { participants?: any[] } | null | undefined
): UngradedParticipant[] => {
    const participants = courseProgress?.participants || [];
    return participants.map((p: any) => {
        const modules: any[] = p.modules || [];
        const submitted = modules.filter((m: any) => m.status === 'Submitted' || m.status === 'Passed').length;
        return {
            email: p.email,
            fullname: p.fullname,
            submitted,
            total: modules.length,
        };
    });
};

/**
 * Derives, for one assignment, every participant's status for the new bulk
 * AI evaluation view (see specs/2026-09-17-bulk-assignment-ai-evaluation.md):
 * "no-submission" (nothing to evaluate), "eligible" (submitted, not yet
 * evaluated), or "done" (a grade OR a saved trainer eval comment already
 * exists - checking grade alone is wrong, since a comment can exist with no
 * numeric grade, and a genuine 0 grade must still count as done).
 */
export const deriveBulkEvaluationRows = (
    courseProgress: { participants?: any[] } | null | undefined,
    assignmentId: number
): BulkEvaluationRow[] => {
    const participants = courseProgress?.participants || [];
    return participants.map((p: any) => {
        const mod = (p.modules || []).find((m: any) => m.assignmentid === assignmentId);
        let status: BulkEvaluationRowStatus = 'no-submission';
        if (mod) {
            const isDone = (mod.grade ?? -1) >= 0 || !!mod.hastrainereval;
            const hasSubmission = mod.status === 'Submitted' || mod.status === 'Passed';
            if (isDone) {
                status = 'done';
            } else if (hasSubmission) {
                status = 'eligible';
            }
        }
        return {
            userid: p.userid,
            email: p.email,
            fullname: p.fullname,
            status,
        };
    });
};
